/**
 * Add Network Site Form Composer
 */

import * as React from 'react'
import { useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  AddNetworkSiteFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddNetworkSiteFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderSelectControl,
  renderTextareaControl,
  renderDateControl,
  renderRadioControl,
  renderCheckboxControl,
  renderButtonControl,
  renderMediaUploaderControl,
  type ControlConfig,
} from '@/forms/formRenderFunctions'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { MapPin } from 'lucide-react'
import type { GetNetworkCorridorsResponse } from '@/types/sensNet'
import { AppLogger } from '@/lib/logger'
import { useConfig } from '@/contexts/ConfigContext'
import { useToast } from '@/contexts/ToastContext'

const log = AppLogger.getLogger(import.meta)

// ── Types ────────────────────────────────────────────────────────────────────

export type AddNetworkSiteComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddNetworkSiteFormData>
  showHeader?: boolean
  submitLabel?: string
  corridorId?: string
  networkSiteNumber?: number
}

// Technology options with display labels
const TECH_OPTIONS = [
  { value: '4G',   label: '4G LTE' },
  { value: '5G',   label: '5G NR'  },
  { value: 'LoRa', label: 'LoRa'   },
  { value: 'Wifi', label: 'Wi-Fi'  },
  { value: 'RTK',  label: 'RTK'    },
]

// Detail fields shown per technology
const TECH_DETAIL_FIELDS: { name: keyof Omit<ReturnType<typeof emptyTechDetail>, 'technology'>; label: string; placeholder: string; type?: string }[] = [
  { name: 'spectrum',         label: 'Spectrum',           placeholder: 'e.g., mmWave, sub-6' },
  { name: 'band',             label: 'Band',               placeholder: 'e.g., Band 7 (2600 MHz)' },
  { name: 'frequencyChannel', label: 'Frequency Channel',  placeholder: 'e.g., 2650 MHz' },
  { name: 'antennaType',      label: 'Antenna Type',       placeholder: 'e.g., Ericsson AIR 6449' },
  { name: 'numberOfSectors',  label: 'Number of Sectors',  placeholder: 'e.g., 3', type: 'number' },
]

function emptyTechDetail(technology: string) {
  return { technology, spectrum: '', band: '', frequencyChannel: '', antennaType: '', numberOfSectors: '' as any }
}

// ── Field validation ─────────────────────────────────────────────────────────

const validateField = (name: keyof AddNetworkSiteFormData, value: unknown): string | null => {
  const schema = (AddNetworkSiteFormSchema as any)._def?.schema || AddNetworkSiteFormSchema
  const shape = schema.shape?.[name]
  if (!shape) return null
  const parsed = shape.safeParse(value)
  return parsed.success ? null : (parsed.error.issues[0]?.message ?? 'Invalid value')
}

// Backend requires only name, address, location, and corridor. Site type, backhaul,
// technologies, structure details, and pictures are all optional (nullish).
const REQUIRED_FIELDS = [
  'corridorId', 'name', 'siteAddress',
  'siteGeoLocation.latitude', 'siteGeoLocation.longitude',
]

// ── Composer ─────────────────────────────────────────────────────────────────

export function AddNetworkSiteComposer(props: AddNetworkSiteComposerProps) {
  const {
    onSuccess, onError, onCancel, initialValues,
    showHeader = true, submitLabel,
    corridorId: propCorridorId,
    networkSiteNumber: propNetworkSiteNumber,
  } = props

  const appConfig = useConfig()
  const { toast } = useToast()
  const useMockUpload = appConfig.appEnv === 'development'
  const queryClient = useQueryClient()
  // Holds the uploaded picture URLs (including dev-mode data URLs) so onSuccess
  // can inject them into the query cache even if the backend doesn't echo them back.
  const pendingSitePicturesRef = useRef<string[]>([])

  const { data: corridorsData } = useApiQuery<GetNetworkCorridorsResponse>('getNetworkCorridors', {})

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequired = (name: string) => REQUIRED_FIELDS.includes(name)

  const upsertNetworkSite = useApiMutation('upsertNetworkSite', 'POST', {
    invalidates: ['getNetworkSites', 'getNetworkSiteByNumber'],
    onSuccess: (responseData: any) => {
      // Resolve site data from various response shapes
      const siteData = responseData?.site ?? responseData?.networkSite ?? responseData
      const siteNum = Number(siteData?.networkSiteNumber ?? 0)

      // Prefer pictures from response; fall back to what we uploaded
      const responseMedia: string[] = siteData?.sitePictures ?? []
      const sitePictures = responseMedia.length > 0 ? responseMedia : pendingSitePicturesRef.current

      log.info('upsertNetworkSite response', {
        siteNum,
        responseMediaCount: responseMedia.length,
        pendingMediaCount: pendingSitePicturesRef.current.length,
        usingPending: responseMedia.length === 0,
      })

      // Store images under a dedicated key so the detail page can read them
      // immediately. The getNetworkSites list is re-fetched after onSuccess fires
      // (overwriting any list-level cache injection), so we use a stable per-site
      // key that survives the re-fetch.
      if (siteNum > 0 && sitePictures.length > 0) {
        queryClient.setQueryData(['site-images', siteNum], sitePictures)
        log.info('Stored sitePictures in dedicated site-images cache', { siteNum, count: sitePictures.length })
      }

      pendingSitePicturesRef.current = []
      onSuccess?.()
    },
    onError: (error) => {
      log.error('Failed to upsert network site:', error)
      pendingSitePicturesRef.current = []
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: AddNetworkSiteFormSchema,
    defaultValues: {
      networkSiteNumber: propNetworkSiteNumber ?? initialValues?.networkSiteNumber,
      corridorId: propCorridorId ?? (initialValues as any)?.corridorId ?? '',
      name: initialValues?.name ?? '',
      siteOwnership: (initialValues as any)?.siteOwnership ?? '',
      siteAddress: initialValues?.siteAddress ?? '',
      siteGeoLocation: initialValues?.siteGeoLocation ?? { latitude: '' as any, longitude: '' as any },
      locationNotes: initialValues?.locationNotes ?? '',
      indoorOrOutdoorSite: initialValues?.indoorOrOutdoorSite ?? 'outdoor',
      verticalStructureHeight: initialValues?.verticalStructureHeight ?? ('' as any),
      outdoorVerticalStructureType: initialValues?.outdoorVerticalStructureType ?? undefined,
      indoorStructureType: initialValues?.indoorStructureType ?? undefined,
      selectedTechnologies: initialValues?.selectedTechnologies ?? [],
      networkTechnologies: initialValues?.networkTechnologies ?? [],
      backhaulCapability: initialValues?.backhaulCapability ?? ('' as any),
      shelterAvailable: initialValues?.shelterAvailable ?? false,
      cabinetAttached: initialValues?.cabinetAttached ?? false,
      sitePictures: initialValues?.sitePictures ?? ([] as any),
      networkSiteLaunchDate: initialValues?.networkSiteLaunchDate ?? '',
    },
    onSubmit: async ({ value }) => {
      const formValue = value as AddNetworkSiteFormData
      const coerced: AddNetworkSiteFormData = {
        ...formValue,
        networkSiteNumber: propNetworkSiteNumber ?? initialValues?.networkSiteNumber,
        corridorId: formValue.corridorId,
        name: formValue.name?.trim() ?? '',
        siteOwnership: (formValue as any).siteOwnership?.trim() || undefined,
        siteAddress: formValue.siteAddress?.trim() ?? '',
        siteGeoLocation: {
          latitude: Number((formValue.siteGeoLocation as any)?.latitude),
          longitude: Number((formValue.siteGeoLocation as any)?.longitude),
        },
        locationNotes: formValue.locationNotes?.trim() || undefined,
        indoorOrOutdoorSite: formValue.indoorOrOutdoorSite,
        verticalStructureHeight: formValue.verticalStructureHeight
          ? Number(formValue.verticalStructureHeight) : undefined,
        outdoorVerticalStructureType:
          formValue.indoorOrOutdoorSite === 'outdoor' &&
          formValue.outdoorVerticalStructureType &&
          (formValue.outdoorVerticalStructureType as any) !== ''
            ? formValue.outdoorVerticalStructureType : undefined,
        indoorStructureType:
          formValue.indoorOrOutdoorSite === 'indoor' &&
          formValue.indoorStructureType &&
          (formValue.indoorStructureType as any) !== ''
            ? formValue.indoorStructureType : undefined,
        selectedTechnologies: Array.isArray(formValue.selectedTechnologies)
          ? formValue.selectedTechnologies : [],
        networkTechnologies: (formValue.networkTechnologies || []).map((tech: any) => ({
          technology: tech.technology,
          spectrum: tech.spectrum?.trim() || '',
          band: tech.band?.trim() || '',
          frequencyChannel: tech.frequencyChannel?.trim() || '',
          numberOfSectors: tech.numberOfSectors ? Number(tech.numberOfSectors) : undefined,
          antennaType: tech.antennaType?.trim() || '',
        })) as any,
        backhaulCapability: formValue.backhaulCapability,
        shelterAvailable: formValue.shelterAvailable ?? false,
        cabinetAttached: formValue.cabinetAttached ?? false,
        sitePictures: Array.isArray(formValue.sitePictures)
          ? formValue.sitePictures.map((key: any) =>
              typeof key === 'string'
                ? { objectKey: key, fileName: key, contentType: 'image/jpeg', fileSize: 0, checksum: '' }
                : key
            )
          : [],
        networkSiteLaunchDate: formValue.networkSiteLaunchDate || undefined,
      } as any

      const parsed = AddNetworkSiteFormSchema.safeParse(coerced)
      if (!parsed.success) {
        log.error('Form validation failed:', JSON.stringify(parsed.error.issues, null, 2))
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as keyof AddNetworkSiteFormData, (meta) => ({
              ...meta, errors: [issue.message],
            }))
          }
        }
        // Surface WHY the form didn't submit instead of returning silently.
        const first = parsed.error.issues[0]
        toast({
          title: 'Cannot save network site',
          description: first
            ? `${String(first.path[0] ?? 'Field')}: ${first.message}`
            : 'Please fix the highlighted fields.',
          type: 'error',
        })
        return
      }

      const apiRequest = transformToApiRequest(parsed.data) as any
      // Keep all picture URLs (including data URLs) for cache injection.
      // The backend request only gets real S3 keys (data: URLs already stripped in schema).
      const { _allSitePictures, ...backendRequest } = apiRequest
      pendingSitePicturesRef.current = _allSitePictures ?? []
      await upsertNetworkSite.mutateAsync(backendRequest)
    },
  })

  const siteType = (form.state.values as any).indoorOrOutdoorSite

  // ── Per-field renderers ───────────────────────────────────────────────────

  const renderTextField = (control: any) => (
    <form.AppField
      key={control.name}
      name={control.name}
      validators={{
        onChange: ({ value }) => validateField(control.name, value) || undefined,
        onBlur:   ({ value }) => validateField(control.name, value) || undefined,
      }}
    >
      {(bound) => renderTextControl(
        { name: control.name, label: control.label, placeholder: control.placeholder,
          required: isRequired(control.name), visibility: true, disabled: control.disabled ?? false,
          type: control.inputType === 'number' ? 'number' : 'text' },
        bound
      )}
    </form.AppField>
  )

  const renderTextareaField = (control: any) => (
    <form.AppField
      key={control.name}
      name={control.name}
      validators={{ onBlur: ({ value }) => validateField(control.name, value) || undefined }}
    >
      {(bound) => renderTextareaControl(
        { name: control.name, label: control.label, placeholder: control.placeholder,
          required: isRequired(control.name), visibility: true, disabled: control.disabled ?? false },
        bound
      )}
    </form.AppField>
  )

  const renderRadioField = (control: any) => (
    <form.AppField
      key={control.name}
      name={control.name}
      validators={{ onBlur: ({ value }) => validateField(control.name, value) || undefined }}
    >
      {(bound) => renderRadioControl(
        { name: control.name, label: control.label, required: isRequired(control.name),
          visibility: true, disabled: control.disabled ?? false, options: control.selectOptions || [] },
        bound
      )}
    </form.AppField>
  )

  const renderSelectField = (control: any) => {
    let options = control.selectOptions || []
    const corridorList = (corridorsData as any)?.content?.networkCorridors ?? (corridorsData as any)?.networkCorridors
    if (control.name === 'corridorId' && corridorList) {
      options = corridorList.map((c: any) => ({
        value: c.networkCorridorNumber.toString(),
        label: c.networkCorridorName,
      }))
    }
    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onChange: ({ value }) => validateField(control.name, value) || undefined,
          onBlur:   ({ value }) => validateField(control.name, value) || undefined,
        }}
      >
        {(bound) => renderSelectControl(
          { name: control.name, label: control.label, placeholder: control.placeholder,
            required: isRequired(control.name), visibility: true, disabled: control.disabled ?? false,
            options },
          bound
        )}
      </form.AppField>
    )
  }

  const renderCheckboxField = (control: any) => (
    <form.AppField
      key={control.name}
      name={control.name}
      validators={{ onBlur: ({ value }) => validateField(control.name, value) || undefined }}
    >
      {(bound) => renderCheckboxControl(
        { name: control.name, label: control.label, required: isRequired(control.name),
          visibility: true, disabled: control.disabled ?? false },
        bound
      )}
    </form.AppField>
  )

  const renderDateField = (control: any) => (
    <form.AppField
      key={control.name}
      name={control.name}
      validators={{ onBlur: ({ value }) => validateField(control.name, value) || undefined }}
    >
      {(bound) => renderDateControl(
        { name: control.name, label: control.label, placeholder: control.placeholder,
          required: isRequired(control.name), visibility: true, disabled: control.disabled ?? false,
          type: 'date' },
        bound
      )}
    </form.AppField>
  )

  const renderMediaUploaderField = (control: any) => (
    <form.AppField key={control.name} name={control.name}>
      {(bound) => {
        const controlConfig: ControlConfig = {
          name: control.name, label: control.label, placeholder: control.placeholder,
          required: isRequired(control.name), visibility: true, disabled: control.disabled ?? false,
          description: control.description, uploader: control.uploader,
        }
        return renderMediaUploaderControl(controlConfig, bound, propNetworkSiteNumber?.toString() ?? 'new', useMockUpload)
      }}
    </form.AppField>
  )

  // ── Network Technologies with inline detail cards ─────────────────────────

  const renderNetworkTechnologiesGroup = () => (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-foreground">Network Technologies</h3>

      {/* Technology checklist */}
      <form.AppField name="selectedTechnologies">
        {(bound) => {
          const selected = (bound.state.value as string[]) || []

          const toggleTech = (tech: string, checked: boolean) => {
            const updated = checked ? [...selected, tech] : selected.filter((t) => t !== tech)
            bound.handleChange(updated)

            // Sync networkTechnologies array
            const current: any[] = (form.getFieldValue('networkTechnologies') as any[]) || []
            if (checked) {
              form.setFieldValue('networkTechnologies', [...current, emptyTechDetail(tech)] as any)
            } else {
              form.setFieldValue('networkTechnologies', current.filter((t) => t.technology !== tech) as any)
            }
          }

          return (
            <div className="space-y-2">
              <Label>
                Technologies
              </Label>
              <div className="rounded-md border p-3 space-y-2">
                {TECH_OPTIONS.map((opt) => (
                  <div key={opt.value} className="flex items-center gap-3 cursor-pointer hover:bg-accent rounded p-2">
                    <Checkbox
                      id={`tech-${opt.value}`}
                      checked={selected.includes(opt.value)}
                      onCheckedChange={(c) => toggleTech(opt.value, c === true)}
                    />
                    <label htmlFor={`tech-${opt.value}`} className="text-sm font-normal cursor-pointer flex-1">
                      {opt.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )
        }}
      </form.AppField>

      {/* Per-technology detail cards */}
      <form.AppField name="networkTechnologies">
        {(bound) => {
          const details: any[] = (bound.state.value as any[]) || []
          if (details.length === 0) return null

          const updateDetail = (tech: string, field: string, value: string) => {
            const updated = details.map((d) =>
              d.technology === tech ? { ...d, [field]: value } : d
            )
            bound.handleChange(updated)
          }

          return (
            <div className="space-y-3">
              {details.map((techDetail) => {
                const techLabel = TECH_OPTIONS.find((o) => o.value === techDetail.technology)?.label ?? techDetail.technology
                return (
                  <div key={techDetail.technology} className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                    <p className="text-sm font-semibold text-slate-700">{techLabel} Details</p>
                    <div className="grid grid-cols-2 gap-3">
                      {TECH_DETAIL_FIELDS.map((f) => (
                        <div key={f.name} className="space-y-1">
                          <Label className="text-xs">{f.label}</Label>
                          <Input
                            type={f.type ?? 'text'}
                            placeholder={f.placeholder}
                            value={techDetail[f.name] ?? ''}
                            onChange={(e) => updateDetail(techDetail.technology, f.name, e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        }}
      </form.AppField>
    </div>
  )

  // ── Group dispatcher ──────────────────────────────────────────────────────

  const renderControl = (control: any) => {
    // Conditional visibility for structure fields
    if (control.name === 'verticalStructureHeight' || control.name === 'outdoorVerticalStructureType') {
      if (siteType !== 'outdoor') return null
    }
    if (control.name === 'indoorStructureType') {
      if (siteType !== 'indoor') return null
    }
    // Network tech sub-fields are rendered inline — skip here
    if (control.name.startsWith('networkTechnologies.') || control.name === 'selectedTechnologies') {
      return null
    }

    if (control.htmlControl === 'textarea')           return renderTextareaField(control)
    if (control.htmlControl === 'radio')              return renderRadioField(control)
    if (control.htmlControl === 'mediaUploader')      return renderMediaUploaderField(control)
    if (control.htmlControl === 'checkbox')           return renderCheckboxField(control)
    if (control.htmlControl === 'date')               return renderDateField(control)
    if (control.htmlControl === 'select' && !control.selectMultiple) return renderSelectField(control)
    return renderTextField(control)
  }

  const renderFormContent = () =>
    groups.map((group) => {
      // Network technologies group has custom rendering
      if (group.formGroupName === 'networkTechnologiesGroup') {
        return <React.Fragment key={group.formGroupName}>{renderNetworkTechnologiesGroup()}</React.Fragment>
      }

      const controls = controlsByGroup.get(group.formGroupName) || []
      if (controls.length === 0) return null

      return (
        <div key={group.formGroupName} className="space-y-4">
          {(group as any).formGroupLabel && (
            <h3 className="text-sm font-semibold text-foreground">{(group as any).formGroupLabel}</h3>
          )}
          {controls.map(renderControl).filter(Boolean)}
        </div>
      )
    })

  // ── Render ────────────────────────────────────────────────────────────────

  const formBody = (
    <>
      {renderFormContent()}

      <div className="pt-4 flex justify-center gap-3">
        {renderButtonControl({
          label: FORM_CONFIG.cancelButton?.label ?? '',
          variant: 'outline',
          onClick: onCancel,
          visibility: !!onCancel,
          type: 'button',
        })}
        {renderButtonControl(
          {
            label: upsertNetworkSite.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel || '')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? ''),
            variant: 'default',
            type: 'submit',
          },
          { SubmitButton: form.SubmitButton }
        )}
      </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

// Re-exports
export {
  AddNetworkSiteFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddNetworkSiteFormData
} from './schema'
